import { forwardRef, useState } from "react";
import { StepProps, StepRef } from "../../types";
import useIsMobile from "components/hooks/useIsMobile";
import { StepHeader } from "components/signup/components/StepHeader";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  Divider,
  Collapse,
} from "@mui/material";
import { NextStepButton } from "components/signup/components/NextStepButton";
import {
  Hammer,
  TreePalm,
  Baby,
  PartyPopper,
  Sparkles,
  Utensils,
  X,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

type ExampleTask = {
  title: string;
  icon: React.ReactElement;
  examples: string[];
};

const EXAMPLE_TASKS: ExampleTask[] = [
  {
    title: "Home services",
    icon: <Hammer size={32} stroke="#198282" strokeWidth={1.5} />,
    examples: ["Find a new handyman", "Book a pest control specialist"],
  },
  {
    title: "Travel and vacation",
    icon: <TreePalm size={32} stroke="#198282" strokeWidth={1.5} />,
    examples: ["Book a beach house for 7 people", "Research car rentals"],
  },
  {
    title: "Children's activities",
    icon: <Baby size={32} stroke="#198282" strokeWidth={1.5} />,
    examples: ["Find beginner ballet lessons", "Register for summer camps"],
  },
  {
    title: "Parties and events",
    icon: <PartyPopper size={32} stroke="#198282" strokeWidth={1.5} />,
    examples: ["Book a bounce house", "Find and wrap a present"],
  },
  {
    title: "Calendar management",
    icon: <Sparkles size={32} stroke="#198282" strokeWidth={1.5} />,
    examples: ["Reschedule PT appointment", "Book recurring date night"],
  },
  {
    title: "Meal planning",
    icon: <Utensils size={32} stroke="#198282" strokeWidth={1.5} />,
    examples: [
      "Find new kid-friendly recipes",
      "Order grocery delivery each week",
    ],
  },
];

const Task = ({
  title,
  icon,
  examples,
}: {
  title: string;
  icon: React.ReactElement;
  examples: string[];
}) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState<boolean>(!isMobile);
  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "225px",
        display: "flex",
        flexDirection: "column",
        // gap: "8px",
      }}
    >
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? "12px" : "8px",
          }}
        >
          {icon}
          <Typography variant="bodyHeavy">{title}</Typography>
        </Box>
        {isMobile && (
          <Box
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            {isOpen ? (
              <ChevronUp color="#8E9598" size={24}></ChevronUp>
            ) : (
              <ChevronDown color="#8E9598" size={24}></ChevronDown>
            )}
          </Box>
        )}
      </Box>
      <Collapse in={isOpen}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            marginTop: "8px",
          }}
        >
          {examples.map((example) => (
            <Box key={example}>
              <Box sx={{ paddingLeft: isMobile ? "44px" : "0px" }}>
                <Typography variant="bodySmall">{example}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const TaskItem = ({
  taskInquiry,
  onRemove,
}: {
  taskInquiry: string;
  onRemove: () => void;
}) => {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        padding: "12px 12px 12px 20px",
        alignItems: "center",
        gap: "20px",
        borderRadius: "12px",
        background: theme.palette.gray[1],
        width: "fit-content",
        minWidth: "100%",
        justifyContent: "space-between",
        animation: "growVertical 0.3s ease-out forwards",
        "@keyframes growVertical": {
          from: { maxHeight: "0px", opacity: 0 },
          to: { maxHeight: "200px", opacity: 1 },
        },
      })}
    >
      {taskInquiry}
      <IconButton onClick={() => onRemove()}>
        <X color="#8E9598" />
      </IconButton>
    </Box>
  );
};

export default forwardRef<StepRef, StepProps>(
  ({ onNext, signupState }, _ref) => {
    const isMobile = useIsMobile();
    const [taskInquiry, setTaskInquiry] = useState<string>("");
    const [taskInquiries, setTaskInquiries] = useState<string[]>(
      signupState?.initialTasks || [],
    );
    const isConsultationFlow = signupState.flowPath === "consultation-details";
    const [loading, setLoading] = useState(false);

    const handleContinue = async () => {
      setLoading(true);
      onNext?.({ initialTasks: taskInquiries });
      setLoading(false);
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          padding: isMobile ? "" : "0 32px",
          gap: isMobile ? "32px" : "64px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: isMobile ? "100%" : "70%",
            maxWidth: "740px",
            gap: "12px",
            marginBottom: isMobile ? "16px" : "32px",
          }}
        >
          <StepHeader
            title={
              isConsultationFlow
                ? "Which tasks would you like help with?"
                : "What can we help with?"
            }
            subtitle={
              isConsultationFlow
                ? "To personalize your consultation, we suggest adding 2-3 tasks you'd like to hand off."
                : "No pressure to list everything - just a couple of tasks will help us match you to the right Advisor."
            }
          />

          {/* Example Tasks */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "24px 6px",
              padding: "24px 24px",
              border: "1px solid #D4D4D4",
              borderRadius: "12px",
              background: "white",
              justifyContent: "space-around",
            }}
          >
            {EXAMPLE_TASKS.map((task, i) => {
              return (
                <Box key={i} sx={{ width: isMobile ? "100%" : "auto" }}>
                  <Task key={task.title} {...task} />
                  {isMobile && i !== EXAMPLE_TASKS.length - 1 ? (
                    <Divider sx={{ marginTop: "16px" }}></Divider>
                  ) : (
                    <></>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Task Input */}
          <Box sx={{ margin: "32px 0px 16px 0" }}>
            <Typography variant="h4" sx={{ marginBottom: "8px" }}>
              My tasks
            </Typography>

            <Box sx={{ marginBottom: "16px" }}>
              {isConsultationFlow ? (
                <Typography variant="body" color="#3D3D3D">
                  When you sign up we'll use these tasks to help match you to an
                  Advisor. The tasks will also be available to view and edit in
                  your Faye Family Hub.
                </Typography>
              ) : (
                <Typography variant="body" color="#3D3D3D">
                  We'll add these to your Advisor's task list and they'll reach
                  out for more details. You'll be able to view and edit these
                  tasks in your Faye Family Hub.
                </Typography>
              )}
            </Box>
            <Box
              display="flex"
              gap={isMobile ? "16px" : undefined}
              {...(isMobile
                ? {
                    flexDirection: "column",
                    alignItems: "end",
                  }
                : {
                    flexDirection: "row",
                    alignItems: "center",
                  })}
            >
              <TextField
                fullWidth
                multiline
                maxRows={4}
                placeholder="Write a short task title..."
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!taskInquiry.trim()) {
                      return;
                    }
                    setTaskInquiries((iq) => [taskInquiry, ...iq]);
                    setTaskInquiry("");
                  }
                }}
                onChange={(e) => {
                  setTaskInquiry(e.target.value);
                }}
                value={taskInquiry}
                sx={{
                  "& .MuiInputBase-root": {
                    color: "inherit",
                    fontSize: "1rem",
                    fontWeight: "500",
                    alignItems: "start",
                    "& textarea": { marginTop: "0", padding: "20px 24px" },
                  },
                }}
              />
              <Box
                sx={{
                  ...(!isMobile && {
                    width:
                      isMobile ||
                      taskInquiry.trim() !== "" ||
                      taskInquiries.length > 0
                        ? "152px"
                        : "0px",
                    overflow: "hidden",
                    transition: "width 0.3s ease-out",
                  }),
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  sx={{
                    width: "110px",
                    whiteSpace: "nowrap",
                    minWidth: "110px",
                    flexShrink: 0,
                  }}
                  variant={taskInquiries.length > 0 ? "outlined" : "contained"}
                  size="small"
                  onClick={() => {
                    if (!taskInquiry.trim()) {
                      return;
                    }
                    setTaskInquiries((iq) => [taskInquiry, ...iq]);
                    setTaskInquiry("");
                  }}
                >
                  Add task
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Task List */}
          {taskInquiries.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: "16px",
                  gap: "16px",
                }}
              >
                {taskInquiries.map((taskInquiry, i) => (
                  <TaskItem
                    key={i}
                    taskInquiry={taskInquiry}
                    onRemove={() => {
                      setTaskInquiries((iq) =>
                        iq.filter((_, idx) => idx !== i),
                      );
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Continue Button */}
          <Box
            sx={{
              alignSelf: "flex-end",
              width: isMobile ? "100%" : "auto",
              display: "flex",
            }}
          >
            <NextStepButton
              skippable={true}
              hasValue={taskInquiries.length > 0}
              continueClick={handleContinue}
              disabled={loading}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);
